import { AccountTree } from "@mui/icons-material";
import { Wrench } from "@procore/core-icons";
import { useCallback, useEffect } from "react";

import type { ToolbarButtonEntry } from "contexts/toolbars";
import { toolbarsActions, useDispatchToolbars } from "contexts/toolbars";
import {
    DEBUG_UTILS_WINDOW_ID,
    OCTREE_DEBUG_WINDOW_ID,
    useDispatchWindows,
    useWindows,
    windowsActions,
} from "contexts/windows";

import { Toolbar } from "../Toolbar";

/** @param props.id Toolbar id. Defaults to `"debug-utils-toolbar"`. */
export function DebugUtilsToolbar({ id = "debug-utils-toolbar" }: { id?: string }) {
    const windows = useWindows();
    const debugUtilsWindow = windows[DEBUG_UTILS_WINDOW_ID];
    const octreeDebugWindow = windows[OCTREE_DEBUG_WINDOW_ID];
    const toolbarDispatch = useDispatchToolbars();
    const dispatchWindows = useDispatchWindows();

    const handleDebugUtilsClick = useCallback(() => {
        dispatchWindows(windowsActions.toggleWindow(DEBUG_UTILS_WINDOW_ID, { elementId: id, placement: "right" }));
    }, [dispatchWindows, id]);

    const handleOctreeDebugClick = useCallback(() => {
        dispatchWindows(windowsActions.toggleWindow(OCTREE_DEBUG_WINDOW_ID, { elementId: id, placement: "right" }));
    }, [dispatchWindows, id]);

    useEffect(() => {
        const buttons: ToolbarButtonEntry[] = [
            {
                config: {
                    id: "debugUtils",
                    icon: <Wrench />,
                    onClick: handleDebugUtilsClick,
                    tooltip: {
                        toolName: "debugUtils",
                        label: "Debug utils",
                        shortcutKey: "",
                        alwaysVisible: false,
                        placement: "right",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: "octreeDebug",
                    icon: <AccountTree />,
                    onClick: handleOctreeDebugClick,
                    tooltip: {
                        toolName: "octreeDebug",
                        label: "Octree debug",
                        shortcutKey: "",
                        alwaysVisible: false,
                        placement: "right",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
        ];
        toolbarDispatch(toolbarsActions.addToolbar({ id, buttons }));

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(id));
        };
    }, [toolbarDispatch, id, handleDebugUtilsClick, handleOctreeDebugClick]);

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.bulkUpdateButtonStates(id, {
                debugUtils: { active: debugUtilsWindow?.state.open ?? false },
                octreeDebug: { active: octreeDebugWindow?.state.open ?? false },
            }),
        );
    }, [toolbarDispatch, id, debugUtilsWindow?.state.open, octreeDebugWindow?.state.open]);

    return <Toolbar id={id} />;
}
